import React, { useEffect, useState } from "react";
import { Plus, X, Calendar } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

export default function BusinessHoursCalendar() {
  const [cfg, setCfg] = useState({
    timezone: "Europe/London",
    working_days: ["Monday","Tuesday","Wednesday","Thursday","Friday"],
    start_time: "09:00", end_time: "18:00",
    holidays: [],
    oncall: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  // Holiday form
  const [hDate,  setHDate]  = useState("");
  const [hName,  setHName]  = useState("");
  // On-call form
  const [ocAgent, setOcAgent] = useState("");
  const [ocFrom,  setOcFrom]  = useState("");
  const [ocTo,    setOcTo]    = useState("");

  const token = localStorage.getItem("token");
  const authH = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

  useEffect(() => {
    fetch(`${API}/api/admin/tickets/business-hours`, { headers: authH() })
      .then(r => r.json())
      .then(d => { if (d && typeof d === "object") setCfg(c => ({ ...c, ...d })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/tickets/business-hours`, {
        method: "PUT", headers: authH(), body: JSON.stringify(cfg),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "✓ Business hours saved." });
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  const toggleDay = (day) => setCfg(c => ({
    ...c,
    working_days: c.working_days.includes(day) ? c.working_days.filter(d => d !== day) : [...c.working_days, day],
  }));

  const addHoliday = () => {
    if (!hDate || !hName) return;
    setCfg(c => ({ ...c, holidays: [...c.holidays, { date: hDate, name: hName }] }));
    setHDate(""); setHName("");
  };

  const removeHoliday = (date) => setCfg(c => ({ ...c, holidays: c.holidays.filter(h => h.date !== date) }));

  const addOnCall = () => {
    if (!ocAgent || !ocFrom || !ocTo) return;
    setCfg(c => ({ ...c, oncall: [...c.oncall, { agent: ocAgent, from: ocFrom, to: ocTo }] }));
    setOcAgent(""); setOcFrom(""); setOcTo("");
  };

  const removeOnCall = (i) => setCfg(c => ({ ...c, oncall: c.oncall.filter((_, idx) => idx !== i) }));

  if (loading) return <div className={`${card} p-5`}><div className="h-32 rounded-lg bg-white/5 animate-pulse" /></div>;

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Business Hours & Holidays</span>
        </div>
        <button onClick={save} disabled={saving}
          className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
          {saving ? "Saving…" : "Save All"}
        </button>
      </div>

      {msg && (
        <div className={`mx-5 mt-3 rounded-lg px-4 py-2 text-xs border ${msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"}`}>
          {msg.text}
        </div>
      )}

      <div className="p-5 space-y-5">

        {/* Core hours */}
        <div>
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Core Hours</p>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={labelCls}>Timezone</label>
              <select className={inputCls} value={cfg.timezone} onChange={e => setCfg(c => ({...c, timezone: e.target.value}))}>
                {["Europe/London","Asia/Kolkata","America/New_York","America/Los_Angeles","Asia/Singapore","Australia/Sydney"].map(tz => <option key={tz}>{tz}</option>)}
              </select></div>
            <div><label className={labelCls}>Start Time</label>
              <input type="time" className={inputCls} value={cfg.start_time} onChange={e => setCfg(c => ({...c, start_time: e.target.value}))} /></div>
            <div><label className={labelCls}>End Time</label>
              <input type="time" className={inputCls} value={cfg.end_time} onChange={e => setCfg(c => ({...c, end_time: e.target.value}))} /></div>
          </div>
        </div>

        {/* Working days */}
        <div>
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2">Working Days</p>
          <div className="flex flex-wrap gap-2">
            {DAYS.map(day => {
              const active = cfg.working_days.includes(day);
              return (
                <button key={day} onClick={() => toggleDay(day)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    active ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 bg-white/5 text-white/40 hover:border-white/20"
                  }`}>
                  {day.slice(0,3)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Holidays */}
        <div>
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2">Public Holidays</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div><label className={labelCls}>Date</label><input type="date" className={inputCls} value={hDate} onChange={e => setHDate(e.target.value)} /></div>
            <div><label className={labelCls}>Name</label><input className={inputCls} value={hName} onChange={e => setHName(e.target.value)} placeholder="Christmas Day" /></div>
            <div className="flex items-end">
              <button onClick={addHoliday} disabled={!hDate || !hName}
                className="flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] disabled:opacity-40 transition-colors w-full justify-center">
                <Plus size={12} /> Add
              </button>
            </div>
          </div>
          <div className="space-y-1.5 max-h-36 overflow-y-auto">
            {cfg.holidays.length === 0 ? <p className="text-white/20 text-xs italic">No holidays added.</p>
            : cfg.holidays.map(h => (
              <div key={h.date} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                <span className="text-xs text-white/60">{h.date}</span>
                <span className="text-xs text-white/50">{h.name}</span>
                <button onClick={() => removeHoliday(h.date)} className="text-white/20 hover:text-red-400 transition-colors"><X size={12} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* On-call rotation */}
        <div>
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2">On-Call Rotation</p>
          <p className="text-xs text-white/25 mb-3">Who handles escalations outside business hours.</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div><label className={labelCls}>Agent / Team</label><input className={inputCls} value={ocAgent} onChange={e => setOcAgent(e.target.value)} placeholder="IT L2" /></div>
            <div><label className={labelCls}>From</label><input type="datetime-local" className={inputCls} value={ocFrom} onChange={e => setOcFrom(e.target.value)} /></div>
            <div><label className={labelCls}>To</label><input type="datetime-local" className={inputCls} value={ocTo} onChange={e => setOcTo(e.target.value)} /></div>
          </div>
          <button onClick={addOnCall} disabled={!ocAgent || !ocFrom || !ocTo}
            className="flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] disabled:opacity-40 transition-colors mb-3">
            <Plus size={12} /> Add On-Call Slot
          </button>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {cfg.oncall.length === 0 ? <p className="text-white/20 text-xs italic">No on-call slots.</p>
            : cfg.oncall.map((oc, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                <span className="text-xs text-white/60 font-medium">{oc.agent}</span>
                <span className="text-xs text-white/30">{oc.from?.replace("T"," ")} → {oc.to?.replace("T"," ")}</span>
                <button onClick={() => removeOnCall(i)} className="text-white/20 hover:text-red-400 transition-colors"><X size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}